/* eslint-disable no-restricted-imports */
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { PostModel } from '../../../data-layer/posts/types';
import { isBloggerBusiness } from '../../../utils/accessService';
import { buildAvatarImageSource } from '../../../utils/buildAvatarImageSource';
import { cropPublicId } from '../../../utils/cropPublicId';
import { useIsMobile } from '../../../utils/getIsMobile';
import { hasAvailableProducts } from '../../../utils/hasAvailableProducts';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useFavoritesEnabled } from '../../../utils/useFavoritesEnabled';
import { Carousel } from '../../carousel/Carousel';
import { CarouselDotsIndicators } from '../../carousel/CarouselDotsIndicators/CarouselDotsIndicators';
import { useIsPostReported } from '../../hooks/useIsPostReported';
import { IconKebab } from '../../icon/IconKebab';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { Avatar } from '../../media/Avatar';
import { PostCardGridIcons } from '../PostCardGridIcons/PostCardGridIcons';
import { PostCardStatus } from '../PostCardStatus/PostCardStatus';
import { makeMediaParams } from '../PostDetailsCard/utils';
import { ProductsPreview } from '../ProductsPreview/ProductsPreview';
import { AVATAR_SIZE, MENU_TRIGGER_SIZE, POST_GRID_CARD_FOOTER_HEIGHT } from './constants';
import { CardActions } from './parts/CardActions/CardActions';
import { CardActionsWithFavorites } from './parts/CardActions/CardActionsWithFavorites';
import { useCardActionsFlags } from './parts/CardActions/hooks/useCardActionsFlags';
import { CardMedia } from './parts/CardMedia/CardMedia';
import { CheckoutBadge } from './parts/CheckoutBadge/CheckoutBadge';
import { ReportedPost } from './parts/ReportedPost/ReportedPost';
import {
    BusinessName,
    CardContainer,
    CardFooter,
    CardMediaPreviewWrapper,
    MediaArea,
} from './styles';
import { PostCardGridProps } from './types';
import { useGridCardPlayer } from './useGridCardPlayer';
import { usePostGridCardKebab } from './usePostGridCardKebab';

export const PostCardGrid: FC<PostCardGridProps> = ({
    post,
    index,
    onReport,
    onGridRowShown,
    onClick,
    onBusinessClick,
    gridColumns,
    isVisible = false,
    addonAfter,
    shareDisabled,
    businessPageDisabled,
    imageAlias,
    onBusinessPage,
    onContextMenuOpen,
    onShare,
    onVisibilityChange,
    onVideoPlay,
    onVideoStart,
    onVideoEnd,
    onVideoPause,
    onHover,
    onHoverCancel,
    onGridCardClick,
    onReaction,
    onBookmark,
    hideReportedPosts = true,
    showFooter = true,
    additionalIcon,
    isCompact,
    isShowStatus,
    actionsBottomOffset,
    hiddenActions,
    withProductsPreview,
    innerRef,
}) => {
    const isMobile = useIsMobile();
    const isReported = useIsPostReported(post.id);
    const isFavoritesEnabled = useFavoritesEnabled();

    const [currentSlide, setCurrentSlide] = useState(0);
    const isSliderEnabled = Number(useExperimentFlag('post_card_slider_enabled')) === 1;
    const { withCheckout } = useCardActionsFlags(post);

    const videoContainerRef = useRef<HTMLDivElement | null>(null);
    const firstMedia = post.content[0]?.media?.[0];
    const ratio = Math.min((firstMedia?.width || 1) / (firstMedia?.height || 1), 1);

    const isFirstRenderRef = useRef(true);

    const handleReport = useCallback(() => {
        onReport?.();
    }, [onReport]);

    const { kebab, openKebab } = usePostGridCardKebab({
        onReport: handleReport,
        onShare,
        onBusinessPage,
        onReaction,
        post,
        shareDisabled,
        businessPageDisabled,
    });

    const handleClick = useCallback(() => {
        onClick ? onClick({ postNumber: index }) : onGridCardClick({ postNumber: index });
    }, [onClick, index, onGridCardClick]);

    const handleBusinessClick = useCallback(() => {
        onBusinessClick ? onBusinessClick() : onBusinessPage?.(false);
    }, [onBusinessClick, onBusinessPage]);

    const handleOpenKebab = useCallback(
        (e: React.MouseEvent) => {
            openKebab(e);
            onContextMenuOpen?.();
        },
        [openKebab, onContextMenuOpen],
    );

    const { handleMouseOff, handleMouseOn, mediaCoverEnabled, spinnerEnabled } = useGridCardPlayer({
        content: post.content,
        containerRef: videoContainerRef,
        imageAlias,
        // Первый запуск видео
        onVideoStart: () => onVideoStart(makeMediaParams(post, 0)),
        // Не первый запуск видео
        onVideoPlay: () => onVideoPlay(makeMediaParams(post, 0)),
        onVideoEnd: () => onVideoEnd(makeMediaParams(post, 0)),
        onVideoPause: () => onVideoPause(makeMediaParams(post, 0)),
    });

    // Хак, чтобы вызывать onGridRowShown при первом рендере
    // isVisible обновится только после скролла
    useEffect(() => {
        if (index + 1 === gridColumns) {
            onGridRowShown?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onGridRowShown, gridColumns]);

    useEffect(() => {
        if (isFirstRenderRef.current) {
            isFirstRenderRef.current = false;

            return;
        }

        if (isVisible && (index + 1) % gridColumns === 0) {
            onGridRowShown?.();
        }

        onVisibilityChange({
            ...makeMediaParams(post, 0),
            isInViewport: isVisible,
            postNumber: index,
            isGrid: true,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, onGridRowShown, index, gridColumns, onVisibilityChange]);

    if (isReported && hideReportedPosts) {
        return <ReportedPost />;
    }

    if (isMobile === null) {
        return null;
    }

    const businessName = isBloggerBusiness(post.business?.type)
        ? post.business?.displayName
        : cropPublicId(post.business?.publicId || '');

    const displayName = businessName || post.ownersData?.author?.displayName;
    const avatarUrl =
        post.business?.avatarUrl || buildAvatarImageSource(post.ownersData?.author?.avatarUrl);

    return (
        <CardContainer
            onMouseEnter={onHover}
            onMouseLeave={onHoverCancel}
            dataTestId={DataTestId.POST_CARD_GRID}
            ref={innerRef}
        >
            {isShowStatus && !isFavoritesEnabled ? (
                <PostCardStatus post={post} top={1} left={1} />
            ) : null}
            <PostCardGridIcons
                post={post}
                additionalIcon={additionalIcon}
                index={index}
                withProductsPreview={withProductsPreview}
                withStatusBadge={isShowStatus}
            />
            <CardMediaPreviewWrapper>
                <MediaArea
                    onMouseEnter={isMobile ? undefined : handleMouseOn}
                    onMouseLeave={isMobile ? undefined : handleMouseOff}
                >
                    {isCompact ? (
                        <CardMedia
                            media={post.content[0]}
                            index={0}
                            ratio={ratio}
                            containerRef={videoContainerRef}
                            imageAlias={imageAlias}
                            mediaCoverEnabled={mediaCoverEnabled}
                            spinnerEnabled={spinnerEnabled}
                            onClick={handleClick}
                        />
                    ) : (
                        <Carousel
                            count={post.content.length}
                            borderRadius={2}
                            overflow="hidden"
                            width="100%"
                            activeIndex={isSliderEnabled ? currentSlide : 0}
                            onSlideChange={isSliderEnabled ? setCurrentSlide : undefined}
                            type="hover"
                            renderItem={(index) => {
                                const media = post.content[index];

                                return (
                                    <CardMedia
                                        key={index}
                                        media={media}
                                        index={index}
                                        ratio={ratio}
                                        containerRef={
                                            media.type === 'video' ? videoContainerRef : undefined
                                        }
                                        imageAlias={imageAlias}
                                        mediaCoverEnabled={mediaCoverEnabled}
                                        spinnerEnabled={spinnerEnabled}
                                        onClick={handleClick}
                                    />
                                );
                            }}
                        />
                    )}
                    {hiddenActions ? null : isFavoritesEnabled ? (
                        <CardActionsWithFavorites
                            bottom={actionsBottomOffset}
                            post={post}
                            index={index}
                            onReaction={onReaction}
                            onBookmark={onBookmark}
                            onClick={handleClick}
                        />
                    ) : (
                        <CardActions
                            bottom={actionsBottomOffset}
                            post={post}
                            index={index}
                            onReaction={onReaction}
                            onBookmark={onBookmark}
                            onClick={handleClick}
                        />
                    )}
                    {withCheckout && !hiddenActions ? (
                        <Box
                            position="absolute"
                            bottom={1}
                            left={isFavoritesEnabled ? undefined : 1}
                            right={isFavoritesEnabled ? 1 : undefined}
                        >
                            <CheckoutBadge onClick={handleClick} />
                        </Box>
                    ) : null}
                </MediaArea>
                {withProductsPreview && hasAvailableProducts(post as PostModel) ? (
                    <ProductsPreview onClick={handleClick} products={post.products.items} />
                ) : null}
            </CardMediaPreviewWrapper>
            {addonAfter}
            <Flex
                position="absolute"
                bottom={`${POST_GRID_CARD_FOOTER_HEIGHT + 6}px`}
                left={0}
                width="100%"
                alignItems="center"
                justifyContent="center"
            >
                {isSliderEnabled ? (
                    <CarouselDotsIndicators
                        slidesCount={post.content.length}
                        activeIndex={currentSlide}
                    />
                ) : null}
            </Flex>
            {showFooter ? (
                <>
                    <CardFooter>
                        <Flex
                            direction="row"
                            alignItems="center"
                            onClick={handleBusinessClick}
                            cursor={onBusinessClick || onBusinessPage ? 'pointer' : 'default'}
                            width={`calc(100% - ${MENU_TRIGGER_SIZE}px)`}
                            spacing={1}
                        >
                            <Avatar
                                width={AVATAR_SIZE}
                                height={AVATAR_SIZE}
                                title={displayName}
                                src={avatarUrl}
                                border="0.5px solid var(--divider)"
                            />

                            <BusinessName dataTestId={DataTestId.BUSINESS_LOGIN}>
                                {displayName}
                            </BusinessName>
                        </Flex>

                        <Box onClick={handleOpenKebab} cursor="pointer">
                            <IconKebab
                                color="var(--color-primary)"
                                fontSize={MENU_TRIGGER_SIZE}
                                dataTestId={DataTestId.POST_CARD_KEBAB}
                            />
                        </Box>
                    </CardFooter>
                </>
            ) : null}
            {kebab()}
        </CardContainer>
    );
};
